<template>
    <main class="time-edit-page" v-loading="loading">
        <section class="time-edit-card">
            <header class="time-edit-header">
                <div class="time-edit-title">
                    <el-button class="time-back" :icon="isNew ? 'el-icon-close' : 'el-icon-arrow-left'"
                               :aria-label="$t('common.back')" @click="goBack"></el-button>
                    <h1>{{isNew ? $t('time.createTitle') : $t('time.editTitle')}}</h1>
                </div>
                <div class="time-edit-actions">
                    <el-button v-if="!isNew" type="danger" plain :disabled="loading || loadFailed || saving"
                               @click="confirmDelete">{{$t('common.delete')}}</el-button>
                    <el-button type="primary" :loading="saving" :disabled="loading || loadFailed || saving"
                               @click="save()">{{$t('common.save')}}</el-button>
                </div>
            </header>

            <el-alert v-if="loadFailed" :title="$t('common.loadFailed')" type="error" :closable="false" show-icon></el-alert>
            <el-form v-else-if="!loading" label-position="top" class="time-edit-form">
                <section class="time-edit-section">
                    <h2>{{$t('common.client')}}</h2>
                    <el-select v-model="form.billClientUUID" filterable clearable style="width: 100%" @change="selectClient"
                               :placeholder="$t('time.selectClient')">
                        <el-option v-for="client in clients" :key="client.innerUUID" :label="client.name" :value="client.innerUUID"></el-option>
                    </el-select>
                </section>

                <section class="time-edit-section time-date-grid">
                    <el-form-item :label="$t('time.start')">
                        <el-date-picker v-model="form.start" type="datetime" format="MMM dd, yyyy HH:mm"
                                        :aria-label="$t('time.start')"
                                        :clearable="false"
                                        :placeholder="$t('time.start')"
                                        style="width: 100%" @change="recalculateDuration"></el-date-picker>
                    </el-form-item>
                    <el-form-item :label="$t('time.end')">
                        <el-date-picker v-model="form.end" type="datetime" format="MMM dd, yyyy HH:mm"
                                        :aria-label="$t('time.end')"
                                        :clearable="false"
                                        :placeholder="$t('time.end')"
                                        style="width: 100%" @change="recalculateDuration"></el-date-picker>
                    </el-form-item>
                    <el-form-item :label="$t('time.duration')">
                        <div class="duration-inputs">
                            <el-input-number v-model="durationHours" :min="0" :max="maximumDurationHours"
                                             :aria-label="$t('time.hours')" @change="recalculateEnd"></el-input-number><span>{{$t('time.hours')}}</span>
                            <el-input-number v-model="durationMinutes" :min="0" :max="59"
                                             :aria-label="$t('time.minutes')" @change="recalculateEnd"></el-input-number><span>{{$t('time.minutes')}}</span>
                        </div>
                    </el-form-item>
                </section>

                <section class="time-edit-section time-rate-grid">
                    <el-form-item :label="$t('time.ratePreset')">
                        <el-select v-model="selectedRateIndex" clearable style="width: 100%" @change="applyRate" :placeholder="$t('time.customRate')">
                            <el-option v-for="(rate, index) in configuredRates" :key="index" :label="`${rate.name} · ${money(rate.rate)}`" :value="index"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item :label="$t('common.rate')">
                        <el-input v-model="form.rate" inputmode="decimal"
                                  :placeholder="$t('common.rate')"></el-input>
                    </el-form-item>
                    <el-form-item :label="$t('time.rateName')">
                        <el-input v-model="form.rateName"
                                  :placeholder="$t('time.rateName')"></el-input>
                    </el-form-item>
                    <el-form-item :label="$t('time.showRateName')">
                        <el-switch v-model="form.showRateName" :active-value="1" :inactive-value="0"
                                   :aria-label="$t('time.showRateName')"></el-switch>
                    </el-form-item>
                </section>

                <section class="time-edit-section">
                    <el-form-item :label="$t('common.description')">
                        <el-input v-model="form.timeDescription" type="textarea" :rows="5"

                                  :placeholder="$t('common.description')"></el-input>
                    </el-form-item>
                </section>

                <section class="time-total-card">
                    <span>{{$t('common.total')}}</span>
                    <strong>{{total == null ? '—' : money(total)}}</strong>
                </section>
            </el-form>
        </section>
    </main>
</template>

<script>
    import moment from 'moment-timezone'
    import {timest, uuid} from '@/utils/validate'
    import {syncCompanySelection} from '@/utils/company'
    import {parseTimeRates, timeEpoch} from '@/utils/timeTracker'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {
        calculateTimeTotal,
        isValidTimeRate,
        MAX_TIME_DURATION_HOURS,
        MAX_TIME_TOTAL_MINUTES,
        validateTimeRecord
    } from '@/utils/timeInputValidation'

    export default {
        data() {
            const now = moment().seconds(0).milliseconds(0)
            return {
                isNew: this.$route.path === '/timeCreate',
                loading: true,
                loadFailed: false,
                saving: false,
                allowRouteLeave: false,
                initialSnapshot: '',
                clients: [],
                configuredRates: [],
                selectedRateIndex: null,
                durationHours: 0,
                durationMinutes: 0,
                maximumDurationHours: MAX_TIME_DURATION_HOURS,
                company: null,
                userSetting: null,
                form: {
                    innerUUID: uuid(null, 37),
                    billClientUUID: '',
                    clientName: '',
                    start: now.toDate(),
                    end: now.toDate(),
                    rate: '0',
                    rateName: '',
                    showRateName: 0,
                    timeDescription: '',
                    billed: 0,
                    itemUUID: '',
                    createDate: timest()
                }
            }
        },
        computed: {
            totalMinutes() {
                return this.durationHours * 60 + this.durationMinutes
            },
            total() {
                if (!isValidTimeRate(this.form.rate) || !Number.isInteger(this.totalMinutes)
                    || this.totalMinutes < 0 || this.totalMinutes > MAX_TIME_TOTAL_MINUTES) {
                    return null
                }
                return calculateTimeTotal(this.form.rate, this.totalMinutes)
            }
        },
        methods: {
            queryClientPage(currentPage) {
                return this.$axios.post('query_user_client_user_web', {
                    params: {currentPage, pageSize: 400, userWebCommonQuery: '', orderName: 'name', orderTpe: 1, delStatus: 0}
                }).then(response => {
                    if (response.data.errorCode !== 0) {
                        throw new Error(`query_user_client_user_web failed: ${JSON.stringify(response.data)}`)
                    }
                    if (!Array.isArray(response.data.dataList) || !Number.isInteger(response.data.totalCount)
                        || response.data.totalCount < 0) {
                        throw new Error('Client list response is invalid')
                    }
                    return response.data
                })
            },
            loadClients() {
                return this.queryClientPage(1).then(firstPage => {
                    const pageCount = Math.ceil(firstPage.totalCount / 400)
                    const remainingRequests = []
                    for (let page = 2; page <= pageCount; page++) {
                        remainingRequests.push(this.queryClientPage(page))
                    }
                    return Promise.all(remainingRequests).then(remainingPages => {
                        const clients = firstPage.dataList.concat(...remainingPages.map(page => page.dataList))
                        if (clients.length !== firstPage.totalCount) {
                            throw new Error('Client list response is incomplete')
                        }
                        return clients.filter(client => client.syncStatus !== 1)
                    })
                })
            },
            async load() {
                this.loading = true
                this.loadFailed = false
                try {
                    const contextResponse = await this.$axios.post('query_setting_and_company', {params: {}})
                    if (contextResponse.data.errorCode !== 0) {
                        throw new Error(`query_setting_and_company failed: ${JSON.stringify(contextResponse.data)}`)
                    }
                    const selectedData = syncCompanySelection(contextResponse.data)
                    if (selectedData == null) {
                        throw new Error('Selected company data is missing')
                    }
                    this.company = selectedData.company
                    this.userSetting = selectedData.setting
                    this.configuredRates = parseTimeRates(this.company.timeRateInfo)
                    if (this.isNew && this.configuredRates.length === 1) {
                        this.selectedRateIndex = 0
                        this.applyRate(0)
                    }

                    const requests = [this.loadClients()]
                    if (!this.isNew) {
                        requests.push(this.$axios.post('query_time_tracker_user_web', {params: {
                            innerUUID: this.$route.query.innerUUID,
                            companyUUID: this.company.innerUUID
                        }}))
                    }
                    const responses = await Promise.all(requests)
                    this.clients = responses[0]
                    if (!this.isNew) {
                        if (responses[1].data.errorCode !== 0) {
                            throw new Error(`query_time_tracker_user_web failed: ${JSON.stringify(responses[1].data)}`)
                        }
                        if (responses[1].data.objectData == null) {
                            throw new Error('Time response is invalid')
                        }
                        this.applyExistingTime(responses[1].data.objectData)
                    }
                    this.initialSnapshot = this.formSnapshot()
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Time tracker load failed', error)
                    this.loadFailed = true
                } finally {
                    this.loading = false
                }
            },
            applyExistingTime(time) {
                validateTimeRecord(time, this.company.innerUUID)
                if (time.innerUUID !== this.$route.query.innerUUID) {
                    throw new Error('Time does not belong to the selected company')
                }
                this.form = {
                    innerUUID: time.innerUUID,
                    billClientUUID: time.billClientUUID,
                    clientName: time.clientName,
                    start: moment.unix(timeEpoch(time.startDate)).toDate(),
                    end: moment.unix(timeEpoch(time.endDate)).toDate(),
                    rate: String(time.rate),
                    rateName: time.rateName,
                    showRateName: time.showRateName,
                    timeDescription: time.timeDescription,
                    billed: time.billed,
                    itemUUID: time.itemUUID,
                    createDate: time.createDate
                }
                this.durationHours = Math.floor(time.totalMinutes / 60)
                this.durationMinutes = time.totalMinutes % 60
            },
            selectClient(innerUUID) {
                if (innerUUID === '') {
                    this.form.clientName = ''
                    return
                }
                const client = this.clients.find(item => item.innerUUID === innerUUID)
                if (client == null) {
                    throw new Error('Selected client is invalid')
                }
                this.form.clientName = client.name
            },
            applyRate(index) {
                if (index === '') {
                    this.selectedRateIndex = null
                    return
                }
                const selected = this.configuredRates[index]
                if (selected == null) {
                    throw new Error('Selected time rate is invalid')
                }
                this.form.rate = String(selected.rate)
                this.form.rateName = selected.name
                this.form.showRateName = selected.showRateName
            },
            recalculateDuration() {
                const minutes = moment(this.form.end).diff(moment(this.form.start), 'minutes')
                if (minutes < 0) {
                    this.$message.error(this.$t('time.endBeforeStart'))
                    return
                }
                this.durationHours = Math.floor(minutes / 60)
                this.durationMinutes = minutes % 60
            },
            recalculateEnd() {
                this.form.end = moment(this.form.start).add(this.totalMinutes, 'minutes').toDate()
            },
            requestParams(syncStatus) {
                if (this.company == null || this.company.innerUUID == null || this.company.innerUUID === '') {
                    throw new Error('Current company is required')
                }
                if (syncStatus === 1) {
                    return {
                        innerUUID: this.form.innerUUID,
                        companyUUID: this.company.innerUUID,
                        fromClient: 3,
                        accessDate: timest(),
                        syncStatus: 1
                    }
                }
                const start = moment(this.form.start).seconds(0).milliseconds(0)
                const end = moment(this.form.end).seconds(0).milliseconds(0)
                if (end.isBefore(start)) {
                    throw new Error(this.$t('time.endBeforeStart'))
                }
                if (!isValidTimeRate(this.form.rate)) {
                    throw new Error(this.$t('time.invalidRate'))
                }
                if (!Number.isInteger(this.totalMinutes) || this.totalMinutes < 0 || this.totalMinutes > MAX_TIME_TOTAL_MINUTES
                    || end.diff(start, 'minutes') !== this.totalMinutes) {
                    throw new Error(this.$t('time.invalidDuration'))
                }
                return {
                    innerUUID: this.form.innerUUID,
                    companyUUID: this.company.innerUUID,
                    fromClient: 3,
                    accessDate: timest(),
                    billClientUUID: this.form.billClientUUID,
                    clientName: this.form.clientName,
                    createDate: this.form.createDate,
                    endDate: String(end.unix()),
                    rate: String(this.form.rate).trim(),
                    rateName: this.form.rateName,
                    showRateName: this.form.showRateName,
                    startDate: String(start.unix()),
                    syncStatus,
                    timeDescription: this.form.timeDescription,
                    total: this.total,
                    totalMinutes: this.totalMinutes,
                    itemUUID: this.form.itemUUID,
                    billed: this.form.billed
                }
            },
            save(afterSave) {
                if (this.loading || this.loadFailed || this.saving) {
                    return
                }
                let params
                try {
                    params = this.requestParams(0)
                } catch (error) {
                    this.$message.error(error.message)
                    return
                }
                this.saving = true
                const endpoint = this.isNew ? 'create_time_tracker' : 'update_time_tracker'
                this.$axios.post(endpoint, {params}).then(response => {
                    if (response.data.errorCode !== 0) {
                        return
                    }
                    const saved = response.data.objectData
                    if (saved == null || saved.innerUUID !== this.form.innerUUID
                        || saved.companyUUID !== this.company.innerUUID) {
                        throw new Error('Saved time response is invalid')
                    }
                    this.allowRouteLeave = true
                    this.$message.success(this.$t('common.saveSuccess'))
                    if (typeof afterSave === 'function') {
                        afterSave()
                    } else {
                        this.$router.push('/time')
                    }
                }).catch(error => {
                    if (error.response == null) {
                        this.$message.error(error.message)
                    }
                }).finally(() => {
                    this.saving = false
                })
            },
            confirmDelete() {
                if (this.loading || this.loadFailed || this.saving) {
                    return
                }
                this.$confirm(this.$t('time.confirmDelete'), this.$t('common.warning'), {
                    confirmButtonText: this.$t('common.delete'), cancelButtonText: this.$t('common.cancel'), type: 'warning'
                }).then(() => this.deleteTime()).catch(action => {
                    if (action !== 'cancel' && action !== 'close') {
                        throw action
                    }
                })
            },
            deleteTime() {
                if (this.loading || this.loadFailed || this.saving) {
                    return
                }
                this.loading = true
                this.$axios.post('update_time_tracker', {params: this.requestParams(1)}).then(response => {
                    if (response.data.errorCode !== 0) {
                        return
                    }
                    const deleted = response.data.objectData
                    if (deleted == null || deleted.innerUUID !== this.form.innerUUID || deleted.syncStatus !== 1
                        || deleted.companyUUID !== this.company.innerUUID) {
                        throw new Error('Deleted time response is invalid')
                    }
                    this.allowRouteLeave = true
                    this.$message.success(this.$t('common.deleteSuccess'))
                    this.$router.push('/time')
                }).catch(error => {
                    if (error.response == null) {
                        this.$message.error(error.message)
                    }
                }).finally(() => {
                    this.loading = false
                })
            },
            goBack() {
                this.$router.push('/time')
            },
            money(value) {
                return xiaoshudianfenge(value, this.userSetting)
            },
            formSnapshot() {
                return JSON.stringify({
                    billClientUUID: this.form.billClientUUID,
                    clientName: this.form.clientName,
                    start: moment(this.form.start).valueOf(),
                    end: moment(this.form.end).valueOf(),
                    rate: String(this.form.rate),
                    rateName: this.form.rateName,
                    showRateName: this.form.showRateName,
                    timeDescription: this.form.timeDescription,
                    durationHours: this.durationHours,
                    durationMinutes: this.durationMinutes
                })
            },
            hasUnsavedChanges() {
                return this.initialSnapshot !== '' && this.formSnapshot() !== this.initialSnapshot
            }
        },
        beforeRouteLeave(to, from, next) {
            if (this.allowRouteLeave || !this.hasUnsavedChanges()) {
                next()
                return
            }
            this.$confirm(this.$t('common.unsavedChangesMsg'), this.$t('common.saveChangesTitle'), {
                confirmButtonText: this.$t('common.confirmSave'),
                cancelButtonText: this.$t('common.discard'),
                type: 'warning',
                distinguishCancelAndClose: true,
                closeOnClickModal: false,
                closeOnPressEscape: false
            }).then(() => {
                this.save(next)
            }).catch(action => {
                if (action === 'cancel') {
                    this.allowRouteLeave = true
                    next()
                } else {
                    next(false)
                }
            })
        },
        mounted() {
            this.load()
        }
    }
</script>

<style scoped>
    .time-edit-page { min-height: 100%; padding: 34px; box-sizing: border-box; background: #f4f7fb; }
    .time-edit-card { max-width: 980px; margin: 0 auto; background: #fff; border: 1px solid #e4eaf3; border-radius: 16px; box-shadow: 0 12px 34px rgba(41, 62, 96, .08); overflow: hidden; }
    .time-edit-header { display: flex; justify-content: space-between; align-items: center; padding: 24px 28px; border-bottom: 1px solid #e9eef5; }
    .time-edit-title { display: flex; align-items: center; gap: 14px; }
    .time-edit-title h1 { margin: 0; color: #17213c; font-size: 24px; }
    .time-back { border: 0; font-size: 18px; }
    .time-edit-form { padding: 28px; }
    .time-edit-section { padding: 22px; margin-bottom: 20px; border: 1px solid #e6ecf4; border-radius: 12px; background: #fff; }
    .time-edit-section h2 { margin: 0 0 16px; font-size: 15px; color: #52627c; }
    .time-date-grid, .time-rate-grid { display: grid; gap: 18px; }
    .time-date-grid { grid-template-columns: 1fr 1fr; }
    .time-date-grid .el-form-item:last-child { grid-column: 1 / -1; }
    .time-rate-grid { grid-template-columns: 1fr 1fr; }
    .duration-inputs { display: flex; align-items: center; gap: 10px; }
    .time-total-card { display: flex; justify-content: space-between; padding: 22px 26px; margin-bottom: 20px; color: #fff; background: #17213c; border-radius: 12px; font-size: 20px; }
    .time-total-card strong { font-size: 25px; }
    @media (max-width: 720px) {
        .time-edit-page {
            padding: 12px;
        }

        .time-date-grid,
        .time-rate-grid {
            grid-template-columns: minmax(0, 1fr);
        }

        .time-edit-section >>> .el-date-editor {
            max-width: 100%;
        }

        .duration-inputs {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
        }

        .duration-inputs >>> .el-input-number {
            width: 100%;
        }
    }

    @media (max-width: 640px) {
        .time-edit-header {
            align-items: stretch;
            flex-direction: column;
            gap: 12px;
            padding: 14px 72px 14px 16px;
        }

        .time-edit-actions {
            width: calc(100% + 56px);
            display: flex;
            justify-content: flex-end;
            gap: 10px;
        }

        .time-edit-form {
            padding: 14px;
        }

        .time-edit-section {
            padding: 18px;
        }
    }
</style>
